import React, { useState, useMemo, useEffect } from 'react';
import { useVitalTests } from '../../hooks/useVitalTests';
import { FaEdit, FaTrash, FaPlus, FaFileExport } from 'react-icons/fa';
import { exportToCSV } from '../../utils/exportUtils';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import PaginationControls from './PaginationControls';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Helper function to get test type tag color
const getTestTypeTagColor = (testType) => {
  switch (testType) {
    case 'TEST':
      return 'text-blue-800 bg-blue-100';
    case 'PACKAGE':
      return 'text-purple-800 bg-purple-100';
    default:
      return 'text-gray-800 bg-gray-100';
  }
};

const VitalTestList = () => {
  const { 
    loading, 
    openEditModal, 
    deleteVitalTest, 
    openCreateModal,
    filteredVitalTests,
    updateFilters,
    filters,
    allVitalTests
  } = useVitalTests();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [testsPerPage] = useState(10);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredVitalTests.length]);
  
  // Get current tests for pagination
  const { currentTests, totalPages } = useMemo(() => {
    const indexOfLastTest = currentPage * testsPerPage;
    const indexOfFirstTest = indexOfLastTest - testsPerPage;
    const current = filteredVitalTests.slice(indexOfFirstTest, indexOfLastTest);
    const total = Math.ceil(filteredVitalTests.length / testsPerPage);
    return { currentTests: current, totalPages: total };
  }, [filteredVitalTests, currentPage, testsPerPage]);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handleSearchChange = (e) => {
    updateFilters({ search: e.target.value });
  };

  const handleTestTypeChange = (e) => {
    updateFilters({ testType: e.target.value });
  };

  const handleHasFastingChange = (e) => {
    updateFilters({ hasFasting: e.target.value });
  };
  
  // Get unique test types
  const testTypes = useMemo(() => {
    const types = new Set();
    allVitalTests.forEach(test => {
      if (test.test_type) {
        types.add(test.test_type);
      }
    });
    return Array.from(types).sort();
  }, [allVitalTests]);

  const handleExport = (dataToExport) => {
    try {
      // Export either all Vital Tests or filtered ones based on parameter
      const data = dataToExport === 'all' ? allVitalTests : filteredVitalTests;
      const timestamp = new Date().toISOString().split('T')[0];
      const exportType = dataToExport === 'all' ? 'all' : 'filtered';
      const recordCount = data.length;
      
      // Export the data
      exportToCSV(data, `vital-tests-export-${timestamp}`);
      
      // Show success toast
      toast.success(`Successfully exported ${recordCount} ${exportType} Vital Tests`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export Vital Tests');
    }
  };

  const handleDelete = (testId) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this vital test?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteVitalTest(testId)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-lg font-semibold">Test Masters</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredVitalTests?.length || 0} records shown (Total: {allVitalTests?.length || 0})
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {/* Test Type Filter */}
              <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.testType}
                  onChange={handleTestTypeChange}
                >
                  <option value="">All Test Types</option>
                  <option value="TEST" className="text-blue-800 bg-blue-50">TEST</option>
                  <option value="PACKAGE" className="text-purple-800 bg-purple-50">PACKAGE</option>
                </select>
                {filters.testType && (
                  <button 
                    onClick={() => updateFilters({ testType: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
              
              {/* Has Fasting Filter */}
              {/* <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.hasFasting}
                  onChange={handleHasFastingChange}
                >
                  <option value="">All Fasting Status</option>
                  <option value="YES">Requires Fasting</option>
                  <option value="NO">No Fasting Required</option>
                </select>
                {filters.hasFasting && (
                  <button 
                    onClick={() => updateFilters({ hasFasting: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div> */}
              
              {/* Search Box */}
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  placeholder="Search by name or code"
                  value={filters.search}
                  onChange={handleSearchChange}
                />
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={openCreateModal}
                className="px-4 py-2 bg-green-100 text-green-600 hover:bg-green-200 rounded-md flex items-center transition-colors duration-200 border border-green-200 whitespace-nowrap"
              >
                <FaPlus className="mr-2" /> Add New
              </button>
              {/* <div className="relative inline-block text-left">
                <button
                  onClick={() => handleExport('all')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export All
                </button>
              </div>
              {filteredVitalTests.length !== allVitalTests.length && (
                <button
                  onClick={() => handleExport('filtered')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export Filtered
                </button>
              )} */}
            </div>
          </div>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-1/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Code
            </th>
            <th scope="col" className="w-3/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Test Name ↓
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center">
                <span>Test Type</span>
                <div className="ml-2 flex space-x-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-blue-100 border border-blue-300"></span>
                  <span className="inline-block w-3 h-3 rounded-full bg-purple-100 border border-purple-300"></span>
                </div>
              </div>
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center bg-gray-100">
              Price
            </th>
            <th scope="col" className="w-1/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
              Fasting
            </th>
            <th scope="col" className="w-3/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {currentTests && currentTests.length > 0 ? currentTests.map((test) => (
            <tr 
              key={test._id || test.id} 
              className="hover:bg-blue-50 transition-colors duration-150"
            >
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900 break-words">{test.test_code}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 break-words">{test.name}</div>
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-start">
                  <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${getTestTypeTagColor(test.test_type)}`}>
                    {test.test_type}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="text-sm break-words">{test.sale_price}</div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className={`text-sm ${test.has_fasting === "YES" ? "text-red-600 font-medium" : "text-green-600"} break-words`}>
                  {test.has_fasting === "YES" ? "Required" : "Not Required"}
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    onClick={() => {
                      const testId = test._id || test.id;
                      if (!testId) {
                        console.error('Error: Missing test ID for edit', test);
                        return;
                      }
                      openEditModal(test);
                    }}
                    className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors duration-200 border border-blue-200"
                  >
                    <FaEdit className="mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      const testId = test._id || test.id;
                      if (!testId) {
                        console.error('Error: Missing test ID for deletion', test);
                        return;
                      }
                      handleDelete(testId);
                    }}
                    className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md flex items-center transition-colors duration-200 border border-red-200"
                  >
                    <FaTrash className="mr-1" /> Delete
                  </button>
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" className="px-6 py-10 text-center">
                <p className="text-gray-500">No Vital Tests found. Try adjusting your search.</p>
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="mt-2 px-4 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md transition-colors duration-200 border border-blue-200"
                  >
                    Clear Search
                  </button>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {totalPages > 1 && (
        <div className="mt-4 px-6 py-3 border-t">
          <PaginationControls 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default VitalTestList;
